// context/ThemeModeContext.tsx
// サイト全体のライト/ダークテーマ切り替え。Cookieに保存し、次回訪問時に復元する。

import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getCookie, setCookie } from '../utils/cookies';

export type ThemeMode = 'light' | 'dark';

const COOKIE_NAME = 'rl_theme_mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

function getInitialMode(): ThemeMode {
  const saved = getCookie(COOKIE_NAME);
  if (saved === 'light' || saved === 'dark') return saved;
  // Cookie未設定時は、これまでの見た目（ダーク基調）を踏襲
  return 'dark';
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    setCookie(COOKIE_NAME, next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      setCookie(COOKIE_NAME, next);
      return next;
    });
  }, []);

  const theme: Theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#1976d2' },
          secondary: { main: '#dc004e' },
          ...(mode === 'dark'
            ? {
                background: { default: '#0f172a', paper: '#1e293b' },
                text: {
                  primary: 'rgba(226,232,240,0.92)',
                  secondary: 'rgba(226,232,240,0.72)',
                },
              }
            : {
                background: { default: '#f5f7fa', paper: '#ffffff' },
                text: {
                  primary: 'rgba(15,23,42,0.92)',
                  secondary: 'rgba(15,23,42,0.65)',
                },
              }),
        },
      }),
    [mode]
  );

  const value = useMemo(() => ({ mode, toggleMode, setMode }), [mode, toggleMode, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return ctx;
}

// ============================================================
// 共通トークン（ページ独自のダーク固定パレットを置き換えるための共有値）
// GraphPage / AuthorsDetailPage 等が個別に持っていた TOKENS と同じ形。
// 新しくページを対応させる際は、この値を使うようにする。
// ============================================================
export interface AppTokens {
  bg: string;
  text: string;
  textSub: string;
  border: string;
  borderSoft: string;
  glass: string;
  glassStrong: string;
  accent: string;
  error: string;
  /** ボタン等の淡い背景（通常時） */
  buttonBg: string;
  /** ボタン等の淡い背景（hover時） */
  buttonBgHover: string;
  /** カード・書影プレースホルダーの背景 */
  cardBg: string;
  /** カード・書影プレースホルダーの背景（hover時） */
  cardBgHover: string;
  /** ページ背景に重ねる淡いグロー（radial-gradient用） */
  radialGlow: string;
  /** グラフキャンバス（2D/3D共通）の背景色 */
  canvasBg: string;
  /** グラフキャンバス上のノードラベル文字色 */
  canvasLabelText: string;
  /** 書影の縁取り線（キャンバス上） */
  canvasOutline: string;
  /** リンク線（2Dキャンバス） */
  canvasLinkColor: string;
  /** 選択中ノードを囲む動く点線の色（キャンバス上） */
  selectionRing: string;
}

const DARK_TOKENS: AppTokens = {
  bg: '#0f172a',
  text: 'rgba(226,232,240,0.92)',
  textSub: 'rgba(226,232,240,0.72)',
  border: 'rgba(55,65,81,0.85)',
  borderSoft: 'rgba(148,163,184,0.35)',
  glass: 'rgba(15,23,42,0.72)',
  glassStrong: 'rgba(15,23,42,0.86)',
  accent: 'rgba(148,163,184,0.75)',
  error: '#f97373',
  buttonBg: 'rgba(15,23,42,0.55)',
  buttonBgHover: 'rgba(15,23,42,0.78)',
  cardBg: 'rgba(2,6,23,0.25)',
  cardBgHover: 'rgba(2,6,23,0.35)',
  radialGlow: 'rgba(30,41,59,0.35)',
  canvasBg: '#0f172a',
  canvasLabelText: 'rgba(226,232,240,0.9)',
  canvasOutline: 'rgba(226,232,240,0.35)',
  canvasLinkColor: 'rgba(148,163,184,0.25)',
  selectionRing: '#ffffff',
};

const LIGHT_TOKENS: AppTokens = {
  bg: '#f5f0e3',
  text: 'rgba(15,23,42,0.92)',
  textSub: 'rgba(15,23,42,0.65)',
  border: 'rgba(148,163,184,0.45)',
  borderSoft: 'rgba(100,116,139,0.3)',
  glass: 'rgba(250,246,236,0.80)',
  glassStrong: 'rgba(250,246,236,0.94)',
  accent: 'rgba(51,65,85,0.75)',
  error: '#d32f2f',
  canvasBg: '#f2ead8',
  canvasLabelText: 'rgba(0,0,0,0.97)',
  canvasOutline: 'rgba(30,41,59,0.42)',
  canvasLinkColor: 'rgba(83,97,120,0.42)',
  selectionRing: '#64748b',
  buttonBg: 'rgba(250,246,236,0.78)',
  buttonBgHover: 'rgba(250,246,236,0.96)',
  cardBg: 'rgba(15,23,42,0.04)',
  cardBgHover: 'rgba(15,23,42,0.08)',
  radialGlow: 'rgba(148,163,184,0.25)',
};

export function useAppTokens(): AppTokens {
  const { mode } = useThemeMode();
  return mode === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
}
